import { useTranslation } from 'react-i18next';
import Alert from '@/components/base/Alert';
import { formatDateTime } from '@/lib/format';
import { buildProfileDiff } from '@/lib/laundryProfile';
import type { LaundryModificationRequest } from '@/types';
import ModificationDiffList from './ModificationDiffList';
import ModificationStatusBadge from './ModificationStatusBadge';

interface ModificationStatusCardProps {
  modification: LaundryModificationRequest | null;
}

const NOTE_KEY: Record<LaundryModificationRequest['status'], string> = {
  under_review: 'settings.mod.underReviewNote',
  approved: 'settings.mod.approvedNote',
  rejected: 'settings.mod.rejectedNote',
};

const NOTE_VARIANT: Record<LaundryModificationRequest['status'], 'info' | 'success' | 'warning'> = {
  under_review: 'info',
  approved: 'success',
  rejected: 'warning',
};

/** Shows the state the system returns for the latest change request. */
export default function ModificationStatusCard({ modification }: ModificationStatusCardProps) {
  const { t, i18n } = useTranslation();

  if (!modification) {
    return (
      <Alert variant="info" title={t('settings.mod.noneTitle')}>
        {t('settings.mod.noneText')}
      </Alert>
    );
  }

  const isApproved = modification.status === 'approved';
  const diffs = buildProfileDiff(modification.previous, modification.proposed, t, i18n.language);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 shadow-xs md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground-950">{t('settings.mod.title')}</h3>
        <ModificationStatusBadge status={modification.status} />
      </div>

      <div className="mt-3">
        <Alert variant={NOTE_VARIANT[modification.status]}>{t(NOTE_KEY[modification.status])}</Alert>
      </div>

      <p className="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
        <i className="ri-history-line text-base text-foreground-400" aria-hidden="true" />
        <span>{t('settings.mod.submittedAt')}:</span>
        <span className="font-medium text-foreground-700">
          {formatDateTime(modification.submittedAt, i18n.language)}
        </span>
      </p>

      {modification.status === 'rejected' && modification.rejectionReason ? (
        <div className="mt-3">
          <Alert variant="error" title={t('settings.mod.rejectionReason')}>
            <span className="whitespace-pre-wrap break-words">{modification.rejectionReason}</span>
          </Alert>
        </div>
      ) : null}

      <div className="mt-4">
        <h4 className="mb-2.5 text-xs font-semibold text-foreground-700">
          {t(isApproved ? 'settings.mod.approvedChangesTitle' : 'settings.mod.changesTitle')}
        </h4>
        <ModificationDiffList
          diffs={diffs}
          fromLabel={t(isApproved ? 'settings.mod.col.previous' : 'settings.mod.col.current')}
          toLabel={t(isApproved ? 'settings.mod.col.approvedValue' : 'settings.mod.col.requested')}
        />
      </div>

      <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
        <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
        {t('settings.mod.eligibilityNote')}
      </p>
    </div>
  );
}